/**
 * CF-TOS-REWRITE-V1: Terms of Service content + version (custodial model).
 *
 * Single source of truth for the TOS text. Consumed by:
 *   - pages/Terms.tsx (renders the full text at /terms)
 *
 * TERMS_VERSION is the canonical ToS version stamp. It was the re-acceptance
 * localStorage key (`tos-accepted-${TERMS_VERSION}`) until the "Before you play"
 * terms gate was removed in CF-TERMS-GATE-REMOVE-V1 (#13). There is no live
 * re-acceptance gate today — the /terms page always shows the current version.
 * The stamp is retained as the version record (and for any future flow).
 *
 * Version history:
 *   v1: initial schema-9 release
 *   v2-schema10: schema-10 OVERBOOK_INDEPENDENT + Trust Model section
 *   v3-custodial: full custodial trust model (R7-E.1 frontend custodial cutover)
 *   v4-2026-06-01: calm-but-honest rewrite (PL #7311) — dropped the defensive
 *     "Trust Model" section, the keys-compromised line, all house-margin (0%)
 *     claims, and the @cricketflip_bot contact; contact -> support@cricketflip.com.
 *     Data + Changes wording corrected to match reality post-#13 (no stored
 *     terms-acceptance flag, no re-acceptance gate).
 */
export const TERMS_VERSION = 'v4-2026-06-01';

export const TERMS_LAST_UPDATED = '2026-06-01';

export const TERMS_TEXT = `# CricketFlip — Terms of Service

Last updated: ${TERMS_LAST_UPDATED}

CricketFlip is a provably-fair coin-toss game played with TON, built for Telegram. Call heads or tails — win, and your bet doubles. Every flip is signed and verifiable. These terms explain how it works and the rules of play.

## Who can play

You must be at least 18, or the legal age for online gaming where you live — whichever is higher. You're responsible for making sure real-money coin-toss games are legal where you are.

We don't accept players from places where online gambling is prohibited or needs a licence we don't hold — including the United States, United Kingdom, France, Spain, Netherlands, Italy and Germany, among others. If it isn't legal for you, please don't play.

## How it works

- Deposit — send TON from your wallet to CricketFlip. Your deposit is credited to your in-game balance, which CricketFlip holds and records in our ledger — the way an exchange holds your balance. Standard TON network gas applies to your own transactions.
- Play — pick heads or tails. A Standard win pays 2× your bet. Boost and Mega flips pay more and carry a small fee, shown before you play.
- Verify — every flip outcome is signed by CricketFlip and given a flip ID, so you can independently verify any result in the app.
- Withdraw — cash out your balance to your wallet whenever you like, usually within seconds.

## Fair play

Every flip is provably fair: we publish the method, so you can check any outcome yourself.

You agree not to manipulate the game through exploits, automation, bots or collusion; not to use the service from a restricted location or for any illegal purpose; and not to play on behalf of someone who isn't eligible. We may block or refund accounts that break these rules.

## Risk

You're playing a real-money game of chance. You can lose what you stake — only play with money you can afford to lose.

CricketFlip holds your balance and processes deposits, play and withdrawals, protected by spending caps, monitoring and manual review of unusual withdrawals. We don't guarantee uninterrupted service, wallet or TON-network reliability, or protection against your own mistakes (wrong address, wrong amount, lost wallet keys). To the maximum extent permitted by law, CricketFlip is not liable for losses from network outages, service interruptions, events beyond our control, or your own decisions. If a withdrawal is ever delayed, contact us and we'll reconcile it.

## Your data

We collect only what we need to run the game:
- your Telegram username and ID (when you open the app in Telegram),
- your TON wallet address,
- on-chain transaction hashes (already public on TON),
- your balance, flip history and withdrawal records,
- anonymous logs for debugging and abuse prevention,
- a little data saved in your device's storage (onboarding progress and your flip receipts).

We don't sell your data or share it for marketing. We may disclose it when legally required. Blockchain data is public and permanent by nature — we can't change or remove it.

## Responsible play

Gambling should be fun, never a way to make money. If it stops being fun, take a break. If you think you may have a problem, please reach out:
- gamblingtherapy.org (global, free)
- begambleaware.org (UK and international)
- or your local support service.

## Changes

We may update these terms from time to time. The current version always applies and is available here in the app — please check back now and then.

## Governing law & contact

This agreement is governed by the laws of Costa Rica, where the operator is incorporated; disputes are subject to Costa Rican courts or arbitration.

Questions or support: support@cricketflip.com, or @cricketflip_official on Telegram.
`;
